// sdvx-info-frontend/src/preview/theme/previewTheme.ts
// Preview 서페이스 테마 스토어.
// 테마 = themes.css의 `.app-root[data-theme="<id>"]` 블록이 정의하는
// CSS 변수(theme contract) 오버라이드 세트. id는 백엔드 THEME 에셋의
// builtinSlug와 1:1로 일치한다 (ProfileAssetSeeder.BUILTIN_THEME_CATALOG).
//
// 소스 오브 트루스는 서버의 User.profileThemeId — 로그인 시 프로필 응답의
// profileTheme(slug)로 동기화하고, localStorage는 다음 부팅의 첫 페인트용
// 캐시로만 쓴다. dev/QA 확인은 `?theme=light` 쿼리 파라미터로 가능.
// useSyncExternalStore로 구독 가능해 장착 즉시 마운트된 레이아웃에 반영된다.

export const PREVIEW_THEME_IDS = [
  'default',
  'light',
  'vivid',
  'vivid-light',
  'heavenly',
  'heavenly-light',
  'vgc',
] as const;
export type PreviewThemeId = (typeof PREVIEW_THEME_IDS)[number];

// 라이트 패밀리 — LIGHT BASE(themes.css)를 상속하는 테마들.
// 히어로 그라데이션 텍스트, 클래스 칩 라이트 팔레트 등의 분기 기준.
const LIGHT_THEME_IDS: readonly PreviewThemeId[] = ['light', 'vivid-light', 'heavenly-light', 'vgc'];

export function isLightPreviewTheme(id: PreviewThemeId): boolean {
  return LIGHT_THEME_IDS.includes(id);
}

const STORAGE_KEY = 'previewTheme';

function isPreviewThemeId(value: string | null | undefined): value is PreviewThemeId {
  return value != null && (PREVIEW_THEME_IDS as readonly string[]).includes(value);
}

function readInitial(): PreviewThemeId {
  if (typeof window === 'undefined') return 'default';
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('theme');
    if (isPreviewThemeId(fromQuery)) {
      localStorage.setItem(STORAGE_KEY, fromQuery);
      return fromQuery;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isPreviewThemeId(stored)) return stored;
  } catch {
    // storage 접근 불가 환경(사파리 프라이빗 등) — 기본 테마로
  }
  return 'default';
}

let current: PreviewThemeId = readInitial();
const listeners = new Set<() => void>();

export function getPreviewTheme(): PreviewThemeId {
  return current;
}

export function subscribePreviewTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setPreviewTheme(id: PreviewThemeId): void {
  if (id === current) return;
  current = id;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // ignore
  }
  listeners.forEach((listener) => listener());
}

// 서버 프로필의 장착 테마(slug) 반영. 알 수 없는 slug(구클라 + 신테마)나
// null(미장착)은 기본 테마로 처리한다.
export function syncPreviewThemeFromProfile(slug: string | null | undefined): void {
  setPreviewTheme(isPreviewThemeId(slug) ? slug : 'default');
}

// data-theme 속성값 — default는 속성 자체를 생략한다.
export function previewThemeAttr(id: PreviewThemeId): string | undefined {
  return id === 'default' ? undefined : id;
}
